import React, { Component } from 'react';
import { sentryConfig } from '../utils/Sentry/config';
import { type PaymentMethodsComponentLoaderProps } from '../types/PaymentMethodsBannerProps';
import { isBannerEvent } from '../utils/Sentry';
import { SentryContext } from '../context/Sentry';
import { LoggerName } from '../utils/fedopsLogger/loggerNames';

export const withSentryWrapper = (ComponentToWrap) => {
  return class SentryErrorBoundary extends Component<PaymentMethodsComponentLoaderProps> {
    state = { error: null as Error | null };

    getSentryInstance() {
      const { LazySentry } = this.props;
      if (!LazySentry) {
        console.warn('LazySentry is not defined');
        return null;
      }

      try {
        return new LazySentry({
          ...sentryConfig,
          beforeSend: (event) => (isBannerEvent(event) ? event : null),
        });
      } catch (e) {
        console.error('Failed to init LazySentry', e);
        return null;
      }
    }

    getErrorTags() {
      const { isSSR, deviceType, meta, currency, amount } = this.props;
      return {
        isSSR,
        deviceType,
        msid: meta?.msid,
        appId: meta?.appDefId,
        visitorId: meta?.visitorId,
        currency,
        amount,
      };
    }

    public captureException = (error: Error) => {
      const { onBannerError } = this.props;
      if (this.state.error === null) {
        this.setState({ error });
      }

      const tags = this.getErrorTags();
      const sentryInstance = this.getSentryInstance();

      this.props.panoramaClient?.logger()?.error(LoggerName.BannerError, { ...tags, error: error?.message });

      if (sentryInstance) {
        sentryInstance.configureScope((scope) => {
          Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
        });
        sentryInstance.captureException(error);
      } else {
        onBannerError?.(error, tags);
      }
    };

    componentDidCatch(error: Error) {
      this.captureException(error);
    }

    render() {
      if (this.state.error) {
        return null;
      }

      return (
        <SentryContext.Provider value={{ captureException: this.captureException }}>
          <ComponentToWrap {...this.props} captureException={this.captureException} />
        </SentryContext.Provider>
      );
    }
  };
};
